<template>
  <div class="flex flex-col">
    <div class="overflow-x-auto">
      <div class="inline-block min-w-full py-2 px-2">
        <div
          class="overflow-hidden shadow-md ring-1 ring-black ring-opacity-5 md:rounded-lg"
        >
          <table
            v-if="tableData && tableData.length"
            class="min-w-full divide-y divide-gray-300 h-96"
          >
            <!-- HEADER -->
            <thead class="bg-gray-50">
              <tr>
                <th
                  v-for="head in header"
                  :key="head.key"
                  @click="head.key ? setSorting(head.key) : null"
                  class="px-3 py-3 text-center text-sm font-semibold cursor-pointer"
                  :class="activeSorting === head.key ? 'underline' : ''"
                >
                  {{ head.text }}
                </th>
              </tr>
            </thead>

            <!-- BODY -->
            <tbody class="divide-y divide-gray-200 bg-white">
              <tr
                v-for="row in tableData"
                :key="row.caller_number"
                class="hover:bg-gray-50"
              >
                <td
                  v-for="col in body"
                  :key="col"
                  class="px-2 py-3 text-sm text-center"
                >
                  <!-- Caller -->
                  <span v-if="col === 'caller_number'">
                    {{ row[col] }}
                  </span>

                  <!-- Response rate with color -->
                  <span
                    v-else-if="col === 'response_rate'"
                    :class="getAnsweredPercentageColor(row[col])"
                    class="rounded-full px-2 py-1"
                  >
                    {{ row[col] }}%
                  </span>

                  <!-- Talking duration -->
                  <span v-else-if="col.includes('talking_duration')">
                    {{ formatDuration(row[col]) }}
                  </span>

                  <!-- Waiting duration -->
                  <span
                    v-else-if="col.includes('waiting_duration')"
                    :class="
                      col === 'waiting_duration_avg'
                        ? getWaitingColor(row[col])
                        : ''
                    "
                    class="rounded-full px-2 py-1"
                  >
                    {{ formatDuration(row[col]) }}
                  </span>

                  <!-- Default numeric -->
                  <span v-else>
                    <router-link
                      v-if="col === 'inbound_total'"
                      :to="{
                        name: 'ExpertStatisticsCallAnalysis',
                        query: {
                          start_date: startDate,
                          end_date: endDate,
                          start_time: pbxTimeSelected[0],
                          end_time: pbxTimeSelected[1],
                          caller_number: concatenateData
                            ? pbxElements.toString()
                            : getCallerResources(row['caller_number']),
                          call_way: 'inbound',
                          call_status: 'all',
                          page: 1,
                          per_page: 50,
                        },
                      }"
                      target="_blank"
                      class="clickable"
                      tag="div"
                    >
                      {{ row[col] }}
                    </router-link>
                    <router-link
                      v-else-if="col === 'inbound_answered'"
                      :to="{
                        name: 'ExpertStatisticsCallAnalysis',
                        query: {
                          start_date: startDate,
                          end_date: endDate,
                          start_time: pbxTimeSelected[0],
                          end_time: pbxTimeSelected[1],
                          caller_number: concatenateData
                            ? pbxElements.toString()
                            : getCallerResources(row['caller_number']),
                          call_way: 'inbound',
                          call_status: 'answered',
                          page: 1,
                          per_page: 50,
                        },
                      }"
                      target="_blank"
                      class="clickable"
                      tag="div"
                    >
                      {{ row[col] }}
                    </router-link>
                    <router-link
                      v-else-if="col === 'inbound_unanswered'"
                      :to="{
                        name: 'ExpertStatisticsCallAnalysis',
                        query: {
                          start_date: startDate,
                          end_date: endDate,
                          start_time: pbxTimeSelected[0],
                          end_time: pbxTimeSelected[1],
                          caller_number: concatenateData
                            ? pbxElements.toString()
                            : getCallerResources(row['caller_number']),
                          call_way: 'inbound',
                          call_status: 'unanswered',
                          page: 1,
                          per_page: 50,
                        },
                      }"
                      target="_blank"
                      class="clickable"
                      tag="div"
                    >
                      {{ row[col] }}
                    </router-link>
                    <span v-else>
                      {{ row[col] }}
                    </span>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- EMPTY STATE -->
          <div v-else class="text-center p-10 bg-white">
            <h3 class="text-lg font-medium text-gray-900">No data found</h3>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";

export default {
  props: [
    "header",
    "body",
    "tableData",
    "currentSortDir",
    "groupData",
    "concatenateData",
    "startDate",
    "endDate",
  ],

  data() {
    return {
      activeSorting: "",
    };
  },

  computed: {
    ...mapGetters([
      "pbx3cxTableConfig",
      "pbxTimeSelected",
      "pbxElements",
      "pbxCallerGroups",
    ]),
  },

  methods: {
    getCallerResources(callerNumber) {
      const matchingGroups = this.pbxCallerGroups.filter(
        (g) => g.type == 99 && g.name === callerNumber,
      );

      // If no match found, return an empty string
      if (matchingGroups.length === 0) return "";

      return matchingGroups
        .flatMap((g) => {
          // Check if resources is a string like '["06...", "05..."]'
          try {
            const parsed =
              typeof g.resources === "string"
                ? JSON.parse(g.resources)
                : g.resources;
            return parsed;
          } catch (e) {
            return g.resources; // Fallback if parsing fails
          }
        })
        .join(",");
    },

    setSorting(sort) {
      this.activeSorting = sort;
      this.$emit("sort", sort);
    },

    formatDuration(seconds) {
      if (!seconds) return "0s";

      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = seconds % 60;

      return [h > 0 ? h + "h" : null, m > 0 ? m + "m" : null, s + "s"]
        .filter(Boolean)
        .join(" ");
    },

    getAnsweredPercentageColor(value) {
      const config = this.pbx3cxTableConfig;
      if (!config) return "";

      if (
        value <= config.answered_percentage_red_value &&
        config.answered_percentage_red_active
      ) {
        return "text-red-500 bg-red-100 font-semibold";
      }

      if (
        value <= config.answered_percentage_orange_value &&
        config.answered_percentage_orange_active
      ) {
        return "text-orange-500 bg-orange-100 font-semibold";
      }

      if (
        value <= config.answered_percentage_yellow_value &&
        config.answered_percentage_yellow_active
      ) {
        return "text-yellow-500 bg-yellow-100 font-semibold";
      }

      if (config.answered_percentage_green_active) {
        return "text-green-600 bg-green-100 font-semibold";
      }

      return "";
    },

    getWaitingColor(value) {
      const config = this.pbx3cxTableConfig;
      if (!config) return "";

      if (
        value > config.duration_avg_answer_user_red_value &&
        config.duration_avg_answer_user_red_active
      ) {
        return "text-red-500 bg-red-100 font-semibold";
      }

      if (
        value > config.duration_avg_answer_user_orange_value &&
        config.duration_avg_answer_user_orange_active
      ) {
        return "text-orange-500 bg-orange-100 font-semibold";
      }

      if (config.duration_avg_answer_user_green_active) {
        return "text-green-600 bg-green-100 font-semibold";
      }

      return "";
    },
  },
};
</script>

<style scoped>
.clickable {
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: color 0.2s ease;
}
/* color: #007bff; */
/* font-weight: 600; */

.clickable:hover {
  background: rgba(0, 0, 0, 0.05);
}
</style>
